import React, { useMemo } from 'react'
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { Box, Typography } from '@mui/material'
import { SEVERITY, SEVERITY_ORDER } from '../makeReady/severity'

ChartJS.register(ArcElement, Tooltip)

// A multi-segment health ring for the whole tracked fleet. Segments are
// clickable and reflect the same active-filter state as the chip row above
// the card grid, so either one drives the same filter.
const FleetHealthDonut = ({ counts, activeLevels, onToggleLevel }) => {
  const total = SEVERITY_ORDER.reduce(
    (sum, level) => sum + (counts[level] || 0),
    0,
  )
  // Untracked items still draw as a grey slice, but they aren't "not ready"
  // - they've never been checked - so the headline % is over tracked only.
  const trackedTotal = total - (counts.untracked || 0)
  const readyPercent = trackedTotal
    ? Math.round(((counts.good || 0) / trackedTotal) * 100)
    : 0

  const data = useMemo(
    () => ({
      labels: SEVERITY_ORDER.map((level) => SEVERITY[level].label),
      datasets: [
        {
          data: SEVERITY_ORDER.map((level) => counts[level] || 0),
          backgroundColor: SEVERITY_ORDER.map((level) =>
            activeLevels.length === 0 || activeLevels.includes(level)
              ? SEVERITY[level].hex
              : `${SEVERITY[level].hex}33`,
          ),
          borderWidth: 2,
          borderColor: '#fff',
          cutout: '68%',
        },
      ],
    }),
    [counts, activeLevels],
  )

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${ctx.raw}`,
          },
        },
      },
      onClick: (_evt, elements) => {
        if (!elements.length) return
        const level = SEVERITY_ORDER[elements[0].index]
        onToggleLevel(level)
      },
      onHover: (evt, elements) => {
        evt.native.target.style.cursor = elements.length ? 'pointer' : 'default'
      },
    }),
    [onToggleLevel],
  )

  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 160, md: 190 },
        height: { xs: 160, md: 190 },
        flexShrink: 0,
      }}
    >
      <Doughnut data={data} options={options} />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography variant="title2" fontSize="1.8rem" lineHeight={1}>
          {trackedTotal ? `${readyPercent}%` : '–'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ready
        </Typography>
      </Box>
    </Box>
  )
}

export default FleetHealthDonut
